import React from 'react';
import BasePageContainer from 'src/components/CommonV2Components/BasePageContainer/BasePageContainer';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { BEACON_CHART_SPACING } from 'src/components/Layout/Beacon/BeaconProLayoutConsts';
import AtlasV2OrganicTrafficV2OrganicTrafficSplineChart from 'src/components/AtlasV2Components/AtlasV2TrafficV2PageComponents/AtlasV2OrganicTrafficV2/AtlasV2OrganicTrafficV2OrganicTrafficSplineChart';
import AtlasV2OrganicTrafficV2OrganicKeywordCountSplineChart from 'src/components/AtlasV2Components/AtlasV2TrafficV2PageComponents/AtlasV2OrganicTrafficV2/AtlasV2OrganicTrafficV2OrganicKeywordCountSplineChart';
import AtlasV2OrganicTrafficV2ShareOfTrafficSplineChart from 'src/components/AtlasV2Components/AtlasV2TrafficV2PageComponents/AtlasV2OrganicTrafficV2/AtlasV2OrganicTrafficV2ShareOfTrafficSplineChart';
import AtlasV2TrafficDropdownTopEntityTraffic from 'src/components/AtlasV2Components/AtlasV2TrafficV2PageComponents/AtlasV2TotalTrafficV2/AtlasV2TrafficDropdownTopEntityTraffic';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { useBrandEntity, useBusinessUnitEntity, useProductEntity } from 'src/utils/Hooks/reduxSelectors';
import { useSegmentEntity } from 'src/utils/Hooks/reduxSelectors';
import AtlasV2TotalFilterableStackedBarChart from 'src/components/AtlasV2Components/AtlasV2TrafficV2PageComponents/AtlasV2TotalTrafficV2/AtlasV2TotalFilterableStackedBarChart';
import { useCategoryEntity, useSubcategoryEntity } from 'src/utils/Hooks/reduxSelectors';
import { AtlasV2TrafficProductGrid } from 'src/components/AtlasV2Components/AtlasV2TrafficV2PageComponents/AtlasV2TotalTrafficV2/AtlasV2TrafficV2ProductGrid';
import AtlasV2OrganicTrafficDoubleDropdownTopEntity from 'src/components/AtlasV2Components/AtlasV2TrafficV2PageComponents/AtlasV2OrganicTrafficV2/AtlasV2OrganicTrafficDoubleDropdownTopEntity';
import AtlasV2FilterableStackedBarChart from 'src/components/AtlasV2Components/AtlasV2TrafficV2PageComponents/AtlasV2TotalTrafficV2/AtlasV2TotalFilterableStackedBarChart';
import AtlasV2TotalTrafficV2BrandedUnbrandedSplineChart from 'src/components/AtlasV2Components/AtlasV2TrafficV2PageComponents/AtlasV2TotalTrafficV2/AtlasV2TrafficV2BrandedUnbrandedSplineChart';
import { shouldShowBrandUnbranded } from 'src/utils/app';
import { useAtlasV2TabTreatmentRedirect } from 'src/components/AtlasV2Components/Hooks/useAtlasV2TabTreatmentRedirect';
import { ChartContextProvider } from 'src/utils/chartContext/ChartContextGroupContext';
import { CHART_CONTEXT_GROUPS, CHART_NAMES } from 'src/utils/chartContext/chartContextRegistry';

/**
 * Atlas V2 Organic Traffic V2 Page layout - Tab name: traffic subtab name: organicClicks
 */
export const AtlasV2OrganicTrafficV2PageLayout = () => {
  const { isCategoryEntity, isAllCategoriesEntity } = useCategoryEntity();
  const { isSubcategoryEntity } = useSubcategoryEntity();
  const { isSegmentEntity } = useSegmentEntity();
  const { isBusinessUnitEntity } = useBusinessUnitEntity();
  const { isBrandEntity } = useBrandEntity();
  const { isProductEntity } = useProductEntity();
  const { isRedirecting } = useAtlasV2TabTreatmentRedirect();
  if (isRedirecting) return null;

  return (
    <BasePageContainer>
      <Flex flexDirection="column" gap={BEACON_CHART_SPACING}>
        {/* Organic Traffic spline */}
        <ChartContextProvider group={CHART_CONTEXT_GROUPS.ORGANIC_V2_TRAFFIC} chartName={CHART_NAMES.ORGANIC_V2_TRAFFIC_SPLINE}>
          <AtlasV2OrganicTrafficV2OrganicTrafficSplineChart />
        </ChartContextProvider>

        {/* Share of Organic Traffic spline */}
        <ChartContextProvider group={CHART_CONTEXT_GROUPS.ORGANIC_V2_SHARE} chartName={CHART_NAMES.ORGANIC_V2_SHARE_SPLINE}>
          <AtlasV2OrganicTrafficV2ShareOfTrafficSplineChart />
        </ChartContextProvider>

        {/* Keyword single dropdown top entity (shared component, hardcodes its own chartName) */}
        {!isCategoryEntity && !isSubcategoryEntity && !isSegmentEntity && !isBusinessUnitEntity && (
          <ChartContextProvider group={CHART_CONTEXT_GROUPS.ORGANIC_V2_TOP_ENTITY_KEYWORD}>
            <AtlasV2TrafficDropdownTopEntityTraffic
              groupByFields={[{ id: 'keyword', label: 'Keyword', indexName: 'traffic' }]}
              queryKey="organicClicks_singleDropdown"
              fields={[
                {
                  fieldName: 'organicClicks',
                  label: 'Organic Traffic',
                  metricType: 'organicClicks',
                  metricTypeDisplay: METRICTYPE.VOLUME
                },
                {
                  fieldName: 'organicClicksShare',
                  label: 'Share of Organic Traffic',
                  metricType: 'organicClicks',
                  metricTypeDisplay: METRICTYPE.PERCENT,
                  isMarketShare: true
                }
              ]}
            />
          </ChartContextProvider>
        )}

        {/* Double dropdown top entity (category/brand) */}
        {(isCategoryEntity || isSubcategoryEntity || isSegmentEntity || isBusinessUnitEntity) && (
          <ChartContextProvider group={CHART_CONTEXT_GROUPS.ORGANIC_V2_TOP_ENTITY} chartName={CHART_NAMES.ORGANIC_V2_TOP_ENTITY_COLUMN}>
            <AtlasV2OrganicTrafficDoubleDropdownTopEntity />
          </ChartContextProvider>
        )}

        {/* Filterable stacked bar (keyword) -- shared component, hardcodes its own chartName */}
        {(isCategoryEntity || isSubcategoryEntity || isSegmentEntity || isBusinessUnitEntity) && (
          <ChartContextProvider group={CHART_CONTEXT_GROUPS.ORGANIC_V2_FILTERABLE_STACKED_BAR_KEYWORD}>
            <AtlasV2TotalFilterableStackedBarChart
              metricDropdownOptions={[
                {
                  id: 'organicTraffic',
                  label: 'Organic Traffic',
                  apiMetricFieldName: 'organicClicks',
                  subtitleLabel: 'organic traffic'
                },
                {
                  id: 'percentTraffic',
                  label: '% of Organic  Traffic',
                  apiMetricFieldName: 'organicClicks',
                  isPercentageVisible: true,
                  defaultMetric: 'organicTraffic',
                  subtitleLabel: 'percentage of organic traffic'
                }
              ]}
              groupByFieldOptions={[
                {
                  id: 'keyword',
                  label: 'Keyword'
                }
              ]}
            />
          </ChartContextProvider>
        )}

        {/* Keyword Count with Organic Traffic spline */}
        <ChartContextProvider group={CHART_CONTEXT_GROUPS.ORGANIC_V2_KEYWORD_COUNT} chartName={CHART_NAMES.ORGANIC_V2_KEYWORD_COUNT_SPLINE}>
          <AtlasV2OrganicTrafficV2OrganicKeywordCountSplineChart />
        </ChartContextProvider>

        {/* Double dropdown top entity (company-level) */}
        {!isCategoryEntity &&
          !isSubcategoryEntity &&
          !isSegmentEntity &&
          !isBusinessUnitEntity &&
          !isProductEntity &&
          !isBrandEntity && (
            <ChartContextProvider group={CHART_CONTEXT_GROUPS.ORGANIC_V2_TOP_ENTITY_COMPANY} chartName={CHART_NAMES.ORGANIC_V2_TOP_ENTITY_COLUMN}>
              <AtlasV2OrganicTrafficDoubleDropdownTopEntity />
            </ChartContextProvider>
          )}

        {/* Double dropdown top entity (brand → category/subcategory) */}
        {isBrandEntity && (
          <ChartContextProvider group={CHART_CONTEXT_GROUPS.ORGANIC_V2_TOP_ENTITY_BRAND} chartName={CHART_NAMES.ORGANIC_V2_TOP_ENTITY_COLUMN}>
            <AtlasV2OrganicTrafficDoubleDropdownTopEntity
              groupByFields={[
                { id: 'category', label: 'Category', indexName: 'traffic' },
                { id: 'subcategory', label: 'Subcategory', indexName: 'traffic' }
              ]}
            />
          </ChartContextProvider>
        )}

        {/* Branded/Unbranded spline (shared component, hardcodes its own chartName) */}
        {(isCategoryEntity || isSubcategoryEntity || isSegmentEntity || isBusinessUnitEntity) &&
          shouldShowBrandUnbranded() && (
            <ChartContextProvider group={CHART_CONTEXT_GROUPS.ORGANIC_V2_BRANDED_UNBRANDED}>
              <AtlasV2TotalTrafficV2BrandedUnbrandedSplineChart fieldName="organicClicks" />
            </ChartContextProvider>
          )}

        {/* Filterable stacked bar (category/subcategory) -- shared component, hardcodes its own chartName */}
        {(isCategoryEntity || isSegmentEntity || isBusinessUnitEntity) && (
          <ChartContextProvider group={CHART_CONTEXT_GROUPS.ORGANIC_V2_FILTERABLE_STACKED_BAR_CATEGORY}>
            <AtlasV2FilterableStackedBarChart
              metricDropdownOptions={[
                {
                  id: 'organicTraffic',
                  label: 'Organic Traffic',
                  apiMetricFieldName: 'organicClicks',
                  subtitleLabel: 'organic traffic'
                },
                {
                  id: 'percentTraffic',
                  label: '% of Organic  Traffic',
                  apiMetricFieldName: 'organicClicks',
                  isPercentageVisible: true,
                  defaultMetric: 'organicTraffic',
                  subtitleLabel: 'percentage of organic traffic'
                }
              ]}
              groupByFieldOptions={[
                ...(isAllCategoriesEntity || isSegmentEntity || isBusinessUnitEntity
                  ? [
                      {
                        id: 'category',
                        label: 'Category'
                      }
                    ]
                  : []),
                {
                  id: 'subcategory',
                  label: 'Subcategory'
                }
              ]}
            />
          </ChartContextProvider>
        )}
        <AtlasV2TrafficProductGrid trafficType="organicClicks" />
      </Flex>
    </BasePageContainer>
  );
};
